/**
 * BaseRepository - Classe abstraite pour tous les repositories
 * Fournit les méthodes communes de gestion d'erreurs et d'exécution de requêtes
 */

import { supabase } from '@/lib/supabase';
import type { PostgrestError } from '@supabase/supabase-js';
import type { ApiError } from '@/types/api.types';

/**
 * Classe de base pour tous les repositories
 * Gère la connexion Supabase et la standardisation des erreurs
 */
export abstract class BaseRepository {
  /**
   * Instance Supabase partagée par tous les repositories
   */
  protected static supabase = supabase;

  /**
   * Convertit une erreur PostgrestError en ApiError standardisée
   */
  protected static handleError(error: PostgrestError, context?: string): ApiError {
    const message = context
      ? `${context}: ${error.message}`
      : error.message;

    return {
      message,
      code: error.code,
      details: error.details ? { details: error.details } : undefined,
      status: this.getHttpStatus(error.code),
    };
  }

  /**
   * Mappe les codes d'erreur PostgreSQL vers des status HTTP
   */
  private static getHttpStatus(code: string): number {
    const statusMap: Record<string, number> = {
      '23505': 409, // Unique violation
      '23503': 409, // Foreign key violation
      '42501': 403, // Insufficient privilege
      '42P01': 404, // Undefined table
      'PGRST116': 404, // Row not found
      'PGRST301': 400, // Invalid request
    };

    return statusMap[code] || 500;
  }

  /**
   * Exécute une requête single et gère les erreurs
   * Utilisé pour SELECT ... LIMIT 1 ou INSERT/UPDATE qui retournent un seul élément
   */
  protected static async executeQuery<T>(
    queryBuilder: PromiseLike<{ data: T | null; error: PostgrestError | null }>,
    context?: string
  ): Promise<T> {
    const { data, error } = await queryBuilder;

    if (error) {
      throw this.handleError(error, context);
    }

    if (!data) {
      throw {
        message: context ? `${context}: No data returned` : 'No data returned',
        code: 'NO_DATA',
        status: 404,
      } as ApiError;
    }

    return data;
  }

  /**
   * Exécute une requête array et gère les erreurs
   * Utilisé pour SELECT qui retourne plusieurs lignes
   */
  protected static async executeQueryArray<T>(
    queryBuilder: PromiseLike<{ data: T[] | null; error: PostgrestError | null }>,
    context?: string
  ): Promise<T[]> {
    const { data, error} = await queryBuilder;

    if (error) {
      throw this.handleError(error, context);
    }

    // Pour les queries array, on retourne un tableau vide si pas de data
    return data || [];
  }

  /**
   * Exécute une requête de comptage et gère les erreurs
   * Utilisé pour les requêtes avec .count()
   */
  protected static async executeCount(
    queryBuilder: PromiseLike<{
      count: number | null;
      error: PostgrestError | null
    }>,
    context?: string
  ): Promise<number> {
    const { count, error } = await queryBuilder;

    if (error) {
      throw this.handleError(error, context);
    }

    return count || 0;
  }

  /**
   * Exécute une mutation (INSERT/UPDATE/DELETE) et gère les erreurs
   * Utilisé pour les opérations de modification
   */
  protected static async executeMutation<T>(
    queryBuilder: PromiseLike<{ data: T | null; error: PostgrestError | null }>,
    context?: string
  ): Promise<T> {
    const { data, error } = await queryBuilder;

    if (error) {
      throw this.handleError(error, context);
    }

    if (!data) {
      throw {
        message: context ? `${context}: Mutation failed` : 'Mutation failed',
        code: 'MUTATION_FAILED',
        status: 500,
      } as ApiError;
    }

    return data;
  }

  /**
   * Exécute plusieurs requêtes en parallèle avec Promise.all
   * Gère les erreurs de manière centralisée
   */
  protected static async executeParallel<T extends readonly unknown[]>(
    queries: readonly [...T],
    context?: string
  ): Promise<{ [K in keyof T]: Awaited<T[K]> }> {
    try {
      const results = await Promise.all(queries);
      return results as { [K in keyof T]: Awaited<T[K]> };
    } catch (error) {
      if (this.isPostgrestError(error)) {
        throw this.handleError(error, context);
      }
      throw error;
    }
  }

  /**
   * Type guard pour vérifier si une erreur est une PostgrestError
   */
  private static isPostgrestError(error: unknown): error is PostgrestError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      'message' in error &&
      'details' in error
    );
  }

  /**
   * Formatte une date en ISO string pour les requêtes
   */
  protected static formatDate(date: Date | string): string {
    if (typeof date === 'string') {
      return new Date(date).toISOString();
    }
    return date.toISOString();
  }

  /**
   * Calcule la date de début pour une période donnée
   */
  protected static getStartDate(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }

  /**
   * Calcule la date de fin (maintenant)
   */
  protected static getEndDate(): string {
    return new Date().toISOString();
  }
}
